import { defineMessages, useIntl } from 'react-intl'
import useViewport from '../../../../hooks/useViewport'
import { Flex, Heading, Stack, Text } from '@homebotapp/hb-react-component-catalog'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useEffect, useState } from 'react'
import { ShortTermRentalGraph } from './ShortTermRentalGraph'
import { formatCurrency } from '../../../../helpers/formatters'
import { getPropertyDetails } from '../../../../store/selectors/airDna'
import { useCountUp } from 'react-countup'
import { ShortTermRentalLearnMoreButton } from './ShortTermRentalLearnMoreButton'
import { ShortTermRentalSliders } from './ShortTermRentalSliders'
import { AirDnaPayload, AirDnaSettings } from '../../../../types/airDna'

interface ShortTermRentalCardExpandedProps {
  listing: Listing
  data: {
    data: AirDnaPayload
    settings: AirDnaSettings
  }
}

export const MSG = defineMessages({
  head: {
    id: 'ShortTermRentalCardExpanded.head',
    defaultMessage: 'Your estimated income'
  },
  subhead: {
    id: 'ShortTermRentalCardExpanded.subhead',
    defaultMessage: 'Based on similar rentals nearby'
  },
  aveMonthlyRevenue: {
    id: 'ShortTermRentalCardExpanded.aveMonthlyRevenue',
    defaultMessage: 'Average monthly revenue'
  },
  totalAnnualRevenue: {
    id: 'ShortTermRentalCardExpanded.totalAnnualRevenue',
    defaultMessage: 'Total annual revenue'
  }
})

export const ShortTermRentalCardExpanded: React.FC<ShortTermRentalCardExpandedProps> = ({ listing, data }) => {
  const intl = useIntl()

  const [price, setPrice] = useState(data.settings?.pricePerNight)
  const [nights, setNights] = useState(data.settings?.nightsPerMonth)

  // Create calculatedData object for the graph and sliders
  const calculatedData = getPropertyDetails({
    data: data.data,
    settings: { pricePerNight: price, nightsPerMonth: nights }
  })

  const { monthlyEarnings, annualEarnings } = calculatedData

  const {
    countUp: countUpMonthly,
    start: startMonthly,
    update: updateMonthly
  } = useCountUp({
    start: 0,
    end: monthlyEarnings,
    duration: 1
  })

  const {
    countUp: countUpYearly,
    start: startYearly,
    update: updateYearly
  } = useCountUp({
    start: 0,
    end: annualEarnings,
    duration: 1
  })

  useEffect(() => {
    updateMonthly(monthlyEarnings)
    updateYearly(annualEarnings)
  }, [monthlyEarnings, annualEarnings])

  useEffect(() => {
    startMonthly()
    startYearly()
  }, [])

  return (
    <Stack gap={0} justifyContent='center'>
      <Heading textAlign='center' mb={0}>
        {intl.formatMessage(MSG.head)}
      </Heading>
      <Text color='neutral-900' textAlign='center' mb={6}>
        {intl.formatMessage(MSG.subhead)}
      </Text>
      <StatBox
        label={intl.formatMessage(MSG.aveMonthlyRevenue)}
        value={formatCurrency(Math.round(countUpMonthly as number))}
      />
      <StatBox
        label={intl.formatMessage(MSG.totalAnnualRevenue)}
        value={formatCurrency(Math.round(countUpYearly as number))}
      />
      <ShortTermRentalGraph listing={listing} data={data.data} calculatedData={calculatedData} />
      <ShortTermRentalSliders
        nights={nights}
        price={price}
        calculatedData={calculatedData}
        setPrice={setPrice}
        setNights={setNights}
      />
      <ShortTermRentalLearnMoreButton />
    </Stack>
  )
}

interface StatBoxProps {
  label: string
  value: string
}

const StatBox = ({ label, value }: StatBoxProps) => {
  const viewport = useViewport()
  return (
    <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems='center' textAlign='center' mb={4}>
      <Heading fontWeight='bold' mb={0} size='sm' color='neutral.900'>
        {label}
      </Heading>
      <Text fontWeight='bold' color='success' size={viewport.isSmall ? 'sm' : 'md'} m={viewport.isSmall ? 0 : 1}>
        {value}
      </Text>
    </Flex>
  )
}
